/**
 * Конфігурація та довідник страхових ризиків для продукту «Нещасний випадок» (СК ІНГО).
 * Для дорослих підтримуються 3 пакети: Комплекс, Дорослі, Лікарняні.
 * Для дітей (вік від 1 до 18 років) активний спеціальний дитячий пакет.
 * Інвалідність 1 і 2 групи об'єднано в один чекбокс, 3-я група вмикається окремо.
 */

export const ACCIDENT_PACKAGES = [
  {
    id: 'complex',
    name: 'Комплекс',
    risks: [
      'RiskDeathAccident',
      'RiskGroupOneTwoAccident',
      'RiskGroupThreeАccident',
      'RiskDeathIllness',
      'RiskGroupOneTwoIllness',
      'RiskGroupThreeIllness',
      'TemporaryDisability01P',
    ],
  },
  {
    id: 'adults',
    name: 'Дорослі',
    risks: [
      'RiskDeathAccident',
      'RiskGroupOneTwoAccident',
      'RiskGroupThreeАccident',
      'RiskInjuriesAccident',
    ],
  },
  {
    id: 'sick_leave',
    name: 'Лікарняні',
    risks: [
      'RiskDeathAccident',
      'RiskGroupOneTwoAccident',
      'RiskGroupThreeАccident',
      'TemporaryDisability03P',
    ],
  },
];

export const CHILD_PACKAGE = {
  id: 'child',
  name: 'Дитяча програма (до 18 років)',
  risks: [
    'RiskDeathAccident',
    'RiskDisabledChild',
    'RiskInjuriesAccident',
  ],
};

export const ALL_ACCIDENT_RISKS = {
  RiskDeathAccident: {
    key: 'RiskDeathAccident',
    name: 'Смерть в результаті нещасного випадку',
    shortName: 'Смерть від НВ',
    description: 'Базовий ризик — входить завжди',
    mandatory: true,
  },
  RiskGroupOneTwoAccident: {
    key: 'RiskGroupOneTwoAccident',
    name: 'Інвалідність 1 та 2 гр. від НВ',
    description: null,
    mandatory: false,
    underlyingKeys: ['RiskGroupOneАccident', 'RiskGroupTwoАccident'],
  },
  RiskGroupThreeАccident: {
    key: 'RiskGroupThreeАccident',
    name: 'Інвалідність 3 гр. від НВ',
    description: null,
    mandatory: false,
    underlyingKeys: ['RiskGroupThreeАccident'],
  },
  RiskInjuriesAccident: {
    key: 'RiskInjuriesAccident',
    name: 'Тілесні ушкодження від НВ',
    description: 'Тілесні ушкодження в результаті нещасного випадку',
    mandatory: false,
  },
  RiskDeathIllness: {
    key: 'RiskDeathIllness',
    name: 'Смерть від хвороби',
    description: null,
    mandatory: false,
  },
  RiskGroupOneTwoIllness: {
    key: 'RiskGroupOneTwoIllness',
    name: 'Інвалідність 1 та 2 гр. від хвороби',
    description: null,
    mandatory: false,
    underlyingKeys: ['RiskGroupOneIllness', 'RiskGroupTwoIllness'],
  },
  RiskGroupThreeIllness: {
    key: 'RiskGroupThreeIllness',
    name: 'Інвалідність 3 гр. від хвороби',
    description: null,
    mandatory: false,
    underlyingKeys: ['RiskGroupThreeIllness'],
  },
  TemporaryDisability03P: {
    key: 'TemporaryDisability03P',
    name: 'Тимчасова втрата працездатності',
    description: 'Тільки НВ · 0,30% за день',
    mandatory: false,
  },
  TemporaryDisability01P: {
    key: 'TemporaryDisability01P',
    name: 'Тимчасова втрата працездатності',
    description: 'Будь-яка причина · 0,10% за день',
    mandatory: false,
  },
  RiskDisabledChild: {
    key: 'RiskDisabledChild',
    name: 'Категорія «дитина-інвалід до 18 років» від НВ',
    description: 'Встановлення Застрахованій особі категорії «дитина-інвалід до досягнення віку 18 років» в результаті нещасного випадку',
    mandatory: false,
    childOnly: true,
  },
};

export function getPackageRisks(packageId, isChild = false) {
  if (isChild) {
    return CHILD_PACKAGE.risks.map(key => ALL_ACCIDENT_RISKS[key]).filter(Boolean);
  }
  const pkg = ACCIDENT_PACKAGES.find(p => p.id === packageId) || ACCIDENT_PACKAGES[1]; // default 'adults'
  return pkg.risks.map(key => ALL_ACCIDENT_RISKS[key]).filter(Boolean);
}

export function getExtraRisks(packageId, isChild = false) {
  if (isChild) {
    return []; // для дітей додаткових ризиків поза дитячим пакетом немає
  }
  const pkg = ACCIDENT_PACKAGES.find(p => p.id === packageId) || ACCIDENT_PACKAGES[1];
  const pkgKeys = new Set(pkg.risks);
  return Object.values(ALL_ACCIDENT_RISKS).filter(r => !r.childOnly && !pkgKeys.has(r.key));
}

export function getDefaultAccidentRisks(packageId = 'adults', isChild = false) {
  if (isChild) {
    return {
      RiskDeathAccident: true,
      RiskDisabledChild: true,
      RiskInjuriesAccident: true,
      RiskGroupOneАccident: false,
      RiskGroupTwoАccident: false,
      RiskGroupThreeАccident: false,
      RiskDeathIllness: false,
      RiskGroupOneIllness: false,
      RiskGroupTwoIllness: false,
      RiskGroupThreeIllness: false,
      TemporaryDisability03P: false,
      TemporaryDisability01P: false,
    };
  }

  const risks = {
    RiskDeathAccident: true,
    RiskInjuriesAccident: false,
    RiskGroupOneАccident: false,
    RiskGroupTwoАccident: false,
    RiskGroupThreeАccident: false,
    RiskDeathIllness: false,
    RiskGroupOneIllness: false,
    RiskGroupTwoIllness: false,
    RiskGroupThreeIllness: false,
    RiskDisabledChild: false,
    TemporaryDisability03P: false,
    TemporaryDisability01P: false,
  };

  const pkg = ACCIDENT_PACKAGES.find(p => p.id === packageId) || ACCIDENT_PACKAGES[1];

  pkg.risks.forEach(k => {
    if (k === 'RiskGroupOneTwoAccident') {
      risks.RiskGroupOneАccident = true;
      risks.RiskGroupTwoАccident = true;
    } else if (k === 'RiskGroupOneTwoIllness') {
      risks.RiskGroupOneIllness = true;
      risks.RiskGroupTwoIllness = true;
    } else {
      risks[k] = true;
    }
  });

  return risks;
}

export function detectPackageFromRisks(risks, isChild = false) {
  if (isChild) return 'child';
  if (!risks) return 'adults';
  const isY = (k) => risks[k] === true || risks[k] === 'Y' || risks[k] === '1' || risks[k] === 1;

  if (isY('TemporaryDisability01P') || isY('RiskDeathIllness') || isY('RiskGroupOneIllness') || isY('RiskGroupTwoIllness')) {
    return 'complex';
  }
  if (isY('TemporaryDisability03P')) {
    return 'sick_leave';
  }
  if (isY('RiskInjuriesAccident')) {
    return 'adults';
  }
  return 'adults';
}

export function buildAccidentRisksPayload(risks, isChild = false) {
  const r = risks || {};
  const isY = (val) => val === true || val === 'Y' || val === '1' || val === 1;

  if (isChild) {
    return {
      RiskDeathAccident: 'Y',
      RiskDeathIllness: 'N',
      RiskGroupOneАccident: 'N',
      RiskGroupOneIllness: 'N',
      RiskGroupTwoАccident: 'N',
      RiskGroupTwoIllness: 'N',
      RiskGroupThreeАccident: 'N',
      RiskGroupThreeIllness: 'N',
      RiskInjuriesAccident: isY(r.RiskInjuriesAccident) ? 'Y' : 'N',
      RiskDisabledChild: isY(r.RiskDisabledChild) ? 'Y' : 'N',
      RiskTemporaryDisability: {
        TemporaryDisability03P: 'N',
        TemporaryDisability01P: 'N',
      },
      riskDeathAccident: 'Y',
      riskDeathIllness: 'N',
      riskGroupOneAccident: 'N',
      riskGroupOneIllness: 'N',
      riskGroupTwoAccident: 'N',
      riskGroupTwoIllness: 'N',
      riskGroupThreeAccident: 'N',
      riskGroupThreeIllness: 'N',
      riskInjuriesAccident: isY(r.RiskInjuriesAccident) ? 'Y' : 'N',
      riskDisabledChild: isY(r.RiskDisabledChild) ? 'Y' : 'N',
      riskTemporaryDisability03P: 'N',
      riskTemporaryDisability01P: 'N',
    };
  }

  return {
    RiskDeathAccident: 'Y',
    RiskDeathIllness: isY(r.RiskDeathIllness) ? 'Y' : 'N',
    RiskGroupOneАccident: isY(r.RiskGroupOneАccident ?? r.RiskGroupOneAccident) ? 'Y' : 'N',
    RiskGroupOneIllness: isY(r.RiskGroupOneIllness) ? 'Y' : 'N',
    RiskGroupTwoАccident: isY(r.RiskGroupTwoАccident ?? r.RiskGroupTwoAccident) ? 'Y' : 'N',
    RiskGroupTwoIllness: isY(r.RiskGroupTwoIllness) ? 'Y' : 'N',
    RiskGroupThreeАccident: isY(r.RiskGroupThreeАccident ?? r.RiskGroupThreeAccident) ? 'Y' : 'N',
    RiskGroupThreeIllness: isY(r.RiskGroupThreeIllness) ? 'Y' : 'N',
    RiskInjuriesAccident: isY(r.RiskInjuriesAccident) ? 'Y' : 'N',
    RiskDisabledChild: 'N',
    RiskTemporaryDisability: {
      TemporaryDisability03P: isY(r.RiskTemporaryDisability?.TemporaryDisability03P ?? r.TemporaryDisability03P) ? 'Y' : 'N',
      TemporaryDisability01P: isY(r.RiskTemporaryDisability?.TemporaryDisability01P ?? r.TemporaryDisability01P) ? 'Y' : 'N',
    },
    // Плоскі ключі для сумісності з різними бекенд-ендпоінтами
    riskDeathAccident: 'Y',
    riskDeathIllness: isY(r.RiskDeathIllness) ? 'Y' : 'N',
    riskGroupOneAccident: isY(r.RiskGroupOneАccident ?? r.RiskGroupOneAccident) ? 'Y' : 'N',
    riskGroupOneIllness: isY(r.RiskGroupOneIllness) ? 'Y' : 'N',
    riskGroupTwoAccident: isY(r.RiskGroupTwoАccident ?? r.RiskGroupTwoAccident) ? 'Y' : 'N',
    riskGroupTwoIllness: isY(r.RiskGroupTwoIllness) ? 'Y' : 'N',
    riskGroupThreeAccident: isY(r.RiskGroupThreeАccident ?? r.RiskGroupThreeAccident) ? 'Y' : 'N',
    riskGroupThreeIllness: isY(r.RiskGroupThreeIllness) ? 'Y' : 'N',
    riskInjuriesAccident: isY(r.RiskInjuriesAccident) ? 'Y' : 'N',
    riskDisabledChild: 'N',
    riskTemporaryDisability03P: isY(r.RiskTemporaryDisability?.TemporaryDisability03P ?? r.TemporaryDisability03P) ? 'Y' : 'N',
    riskTemporaryDisability01P: isY(r.RiskTemporaryDisability?.TemporaryDisability01P ?? r.TemporaryDisability01P) ? 'Y' : 'N',
  };
}

export function formatCoveredRisksCount(risks, isChild = false) {
  if (!risks) return '';
  const r = risks || {};
  let count = 0;
  const isY = (val) => val === true || val === 'Y' || val === '1' || val === 1;

  if (isChild || isY(r.RiskDisabledChild ?? r.riskDisabledChild)) {
    if (isY(r.RiskDeathAccident ?? r.riskDeathAccident ?? true)) count++;
    if (isY(r.RiskDisabledChild ?? r.riskDisabledChild)) count++;
    if (isY(r.RiskInjuriesAccident ?? r.riskInjuriesAccident)) count++;
    return `${count} з 3`;
  }

  if (isY(r.RiskDeathAccident ?? r.riskDeathAccident ?? true)) count++;
  // Для 1-2 групи рахуємо як один ризик для UI
  if (isY(r.RiskGroupOneАccident ?? r.RiskGroupOneAccident ?? r.riskGroupOneAccident) ||
      isY(r.RiskGroupTwoАccident ?? r.RiskGroupTwoAccident ?? r.riskGroupTwoAccident)) {
    count++;
  }
  if (isY(r.RiskGroupThreeАccident ?? r.RiskGroupThreeAccident ?? r.riskGroupThreeAccident)) count++;
  if (isY(r.RiskInjuriesAccident ?? r.riskInjuriesAccident)) count++;
  if (isY(r.RiskDeathIllness ?? r.riskDeathIllness)) count++;
  if (isY(r.RiskGroupOneIllness ?? r.riskGroupOneIllness) || isY(r.RiskGroupTwoIllness ?? r.riskGroupTwoIllness)) {
    count++;
  }
  if (isY(r.RiskGroupThreeIllness ?? r.riskGroupThreeIllness)) count++;

  const temp03 = r.RiskTemporaryDisability?.TemporaryDisability03P ?? r.TemporaryDisability03P ?? r.riskTemporaryDisability03P;
  const temp01 = r.RiskTemporaryDisability?.TemporaryDisability01P ?? r.TemporaryDisability01P ?? r.riskTemporaryDisability01P;
  if (isY(temp03) || isY(temp01)) count++;

  return count ? `${count}` : '';
}
